import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import api from '../../services/api';
import { formatYmdLocal, lastNDaysRange } from '../../utils/trainingHistoryRange';
import { BRAND_CHART_CSS_PRIMARY, resolveBrandChartColors } from '../../utils/brandChartColors';

function dayLabel(fecha) {
  try {
    return new Date(`${fecha}T12:00:00`).toLocaleDateString('es-CO', {
      day: 'numeric',
      month: 'short',
    });
  } catch {
    return String(fecha).slice(5);
  }
}

function resolveColors() {
  return resolveBrandChartColors();
}

const CARDS = {
  asistencia: {
    id: 'asistencia',
    title: 'Asistencia',
    subtitle: 'Clases con asistencia en el ciclo',
    empty: 'Cuando asistas a clases en vivo, verás aquí tu evolución de asistencia.',
    series: [
      { id: 'acumulado', label: 'Acumulado', unit: '', axis: 'left', type: 'area' },
      { id: 'clases', label: 'Clases/día', unit: '', axis: 'right', type: 'line', dash: true },
    ],
  },
  pruebas: {
    id: 'pruebas',
    title: 'Pruebas físicas',
    subtitle: 'Repeticiones y total por registro',
    empty: 'Cuando registres una prueba física del ciclo, verás aquí tu evolución.',
    series: [
      { id: 'total', label: 'Total', unit: '', axis: 'left', type: 'area' },
      { id: 'abdominales', label: 'Abdominales', unit: 'reps', axis: 'right', type: 'line' },
      { id: 'burpees', label: 'Burpees', unit: 'reps', axis: 'right', type: 'line', dash: true },
      { id: 'flexiones', label: 'Flexiones', unit: 'reps', axis: 'right', type: 'line' },
      { id: 'sentadilla', label: 'Sentadilla', unit: 'reps', axis: 'right', type: 'line' },
      { id: 'plancha', label: 'Plancha', unit: 's', axis: 'right', type: 'line' },
    ],
  },
};

function ChartCard({ cfg, rows, colors }) {
  const defaults = Object.fromEntries(cfg.series.map((s) => [s.id, true]));
  const [visible, setVisible] = useState(defaults);

  const hasAny = useMemo(
    () => rows.some((r) => cfg.series.some((s) => visible[s.id] && r[s.id] != null)),
    [rows, visible, cfg.series],
  );

  const colorFor = (idx) => [colors.b, colors.a, colors.c, colors.d, colors.a, colors.c][idx % 6];

  return (
    <div className="card p-4 sm:p-5 space-y-3 overflow-hidden">
      <div>
        <h3 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide m-0">
          {cfg.title}
        </h3>
        <p className="text-xs d28d-text-muted m-0 mt-1">{cfg.subtitle}</p>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {cfg.series.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            className={`text-xs px-2.5 py-1 rounded-full border transition-opacity ${
              visible[s.id]
                ? 'opacity-100 border-[var(--shell-primary,var(--d28d-yellow,#ffd700))] bg-[color-mix(in_srgb,var(--shell-primary,var(--d28d-yellow,#ffd700))_18%,transparent)] text-[var(--d28d-text)]'
                : 'opacity-50 border-white/15 text-[var(--d28d-text)]'
            }`}
            onClick={() => setVisible((prev) => ({ ...prev, [s.id]: !prev[s.id] }))}
          >
            <span
              className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
              style={{ background: colorFor(idx) }}
            />
            {s.label}
          </button>
        ))}
      </div>

      {!hasAny ? (
        <p className="text-sm d28d-text-muted m-0 border border-dashed border-white/15 rounded-xl p-6 text-center">
          {cfg.empty}
        </p>
      ) : (
        <div className="h-56 sm:h-64 w-full min-w-0">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
              <defs>
                <linearGradient id={`d28dFill-${cfg.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={colors.b} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={colors.b} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="currentColor" strokeOpacity={0.08} vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                width={36}
                allowDecimals={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={{ fontSize: 11, fill: 'currentColor', opacity: 0.55 }}
                axisLine={false}
                tickLine={false}
                width={32}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: 'var(--d28d-surface, #18181b)',
                  border: `1px solid color-mix(in srgb, ${BRAND_CHART_CSS_PRIMARY} 35%, transparent)`,
                  borderRadius: 12,
                  color: 'var(--d28d-text, #fafafa)',
                  fontSize: 12,
                }}
                labelFormatter={(_, payload) => {
                  const row = payload?.[0]?.payload;
                  return row?.fecha ? formatYmdLocal(new Date(`${row.fecha}T12:00:00`)) : '';
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: 12, paddingTop: 8 }}
                formatter={(value) => (
                  <span className="text-[var(--d28d-text)] opacity-80">{value}</span>
                )}
              />
              {cfg.series.map((s, idx) => {
                if (!visible[s.id]) return null;
                const stroke = colorFor(idx);
                const yAxisId = s.axis === 'right' ? 'right' : 'left';
                if (s.type === 'area') {
                  return (
                    <Area
                      key={s.id}
                      yAxisId={yAxisId}
                      type="monotone"
                      dataKey={s.id}
                      name={s.label}
                      stroke={stroke}
                      fill={`url(#d28dFill-${cfg.id})`}
                      strokeWidth={2.5}
                      dot={{ r: 3, strokeWidth: 0, fill: stroke }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  );
                }
                return (
                  <Line
                    key={s.id}
                    yAxisId={yAxisId}
                    type="monotone"
                    dataKey={s.id}
                    name={s.label}
                    stroke={stroke}
                    strokeWidth={2}
                    strokeDasharray={s.dash ? '4 3' : undefined}
                    dot={{ r: 3, strokeWidth: 0, fill: stroke }}
                    activeDot={{ r: 5 }}
                    connectNulls
                  />
                );
              })}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

/**
 * Evolución D28D para usuario final: asistencia + pruebas físicas.
 * Misma estética de líneas que Training (sin logs de gimnasio/deportes).
 */
export default function D28dAthleteEvolutionChart({
  sedeMode = false,
  refreshKey = 0,
}) {
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [fitness, setFitness] = useState([]);
  const [colors, setColors] = useState(resolveColors());

  useEffect(() => {
    setColors(resolveColors());
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/d28d/progress/me');
      const data = res.data?.data || {};
      setAttendance(Array.isArray(data.attendance_series) ? data.attendance_series : []);
      setFitness(Array.isArray(data.fitness_series) ? data.fitness_series : []);
    } catch {
      setAttendance([]);
      setFitness([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const filterRange = useCallback((rows) => {
    if (days <= 0) return rows;
    const range = lastNDaysRange(days);
    return (rows || []).filter((r) => r.fecha >= range.from && r.fecha <= range.to);
  }, [days]);

  const attendanceRows = useMemo(
    () => filterRange(attendance).map((r) => ({
      ...r,
      label: dayLabel(r.fecha),
    })),
    [attendance, filterRange],
  );

  const fitnessRows = useMemo(
    () => filterRange(fitness).map((r) => ({
      ...r,
      label: dayLabel(r.fecha),
    })),
    [fitness, filterRange],
  );

  const showAsistencia = attendanceRows.length > 0 || !loading;
  const showPruebas = !sedeMode && (fitnessRows.length > 0 || !loading);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="font-bold text-[var(--d28d-text)] text-sm uppercase tracking-wide m-0">
            Mi evolución D28D
          </h2>
          <p className="text-xs d28d-text-muted m-0 mt-1">
            {sedeMode
              ? 'Asistencia a clases a lo largo del tiempo'
              : 'Asistencia y pruebas físicas a lo largo del tiempo'}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: '30 días', value: 30 },
            { label: '90 días', value: 90 },
            { label: 'Todo', value: 0 },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              className={days === p.value ? 'btn-primary text-xs' : 'btn-secondary text-xs'}
              onClick={() => setDays(p.value)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="card p-4 sm:p-5">
          <div className="h-52 animate-pulse rounded-xl bg-white/5" />
        </div>
      ) : (
        <div className="space-y-3">
          {showAsistencia ? (
            <ChartCard cfg={CARDS.asistencia} rows={attendanceRows} colors={colors} />
          ) : null}
          {showPruebas ? (
            <ChartCard cfg={CARDS.pruebas} rows={fitnessRows} colors={colors} />
          ) : null}
        </div>
      )}
    </div>
  );
}
